import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  DollarSign,
  Plus,
  Edit,
  Trash2,
  Search,
  Filter,
  X,
  BookOpen,
  Calendar,
  Layers,
  ChevronDown,
  CheckCircle,
  AlertCircle,
  BadgePercent,
  IndianRupee,
  CreditCard,
  FileText,
  Eye,
  Copy,
  TrendingUp,
  RefreshCw,
} from "lucide-react";
import axios from "axios";
import { server } from "../server";
import { toast } from "react-toastify";

const FeeStructurePage = () => {
  const [feeStructures, setFeeStructures] = useState([]);
  const [loading, setLoading] = useState(false);
  const [courses, setCourses] = useState([]);
  const [loadingCourses, setLoadingCourses] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingFee, setEditingFee] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [viewingFee, setViewingFee] = useState(null);
  const [formData, setFormData] = useState({
    courseId: "",
    amount: "",
    description: "",
    installments: "1",
    discountPercentage: "0",
    lateFee: "",
    isActive: true,
  });

  // Fetch courses on component mount
  useEffect(() => {
    fetchCourses();
    fetchFeeStructures();
  }, []);

  const fetchFeeStructures = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${server}/fee-structure/all`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        params: {
          pageNo: 1,
          pageSize: 100,
        },
      });
      if (data.success && data.data) {
        setFeeStructures(data.data);
      }
    } catch (error) {
      toast.error("Failed to fetch fee structures");
    } finally {
      setLoading(false);
    }
  };

  const fetchCourses = async () => {
    try {
      setLoadingCourses(true);
      const { data } = await axios.get(`${server}/course/all`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      if (data.success && data.data) {
        setCourses(data.data);
      }
    } catch (error) {
      toast.error("Failed to fetch courses");
    } finally {
      setLoadingCourses(false);
    }
  };

  // Helper function to get course name by ID
  const getCourseName = (courseId) => {
    const course = courses.find((c) => c.id === courseId);
    return course ? course.courseName : "Unknown Course";
  };

  // Helper function to get course code by ID
  const getCourseCode = (courseId) => {
    const course = courses.find((c) => c.id === courseId);
    return course ? course.courseCode : "";
  };

  const resetForm = () => {
    setFormData({
      courseId: "",
      amount: "",
      description: "",
      installments: "1",
      discountPercentage: "0",
      lateFee: "",
      isActive: true,
    });
    setEditingFee(null);
  };

  const handleSaveFee = async () => {
    if (!formData.courseId || !formData.amount) {
      toast.error("Please fill in all required fields");
      return;
    }

    setSubmitting(true);

    const payload = {
      courseId: Number(formData.courseId),
      amount: Number(formData.amount),
      description: formData.description,
      installments: Number(formData.installments),
      discountPercentage: Number(formData.discountPercentage),
      lateFee: Number(formData.lateFee) || 0,
      isActive: formData.isActive,
    };

    try {
      if (editingFee) {
        await axios.put(`${server}/fee-structure/updateById`, payload, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          params: {
            feeStructureId: editingFee.id,
          },
        });
        toast.success("Fee structure updated successfully!");
      } else {
        await axios.post(`${server}/fee-structure/new`, payload, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });
        toast.success("Fee structure created successfully!");
      }

      setShowCreateModal(false);
      resetForm();
      fetchFeeStructures();
    } catch (error) {
      toast.error(
        editingFee
          ? "Failed to update fee structure"
          : "Failed to create fee structure"
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this fee structure?")) {
      setActionLoading({ id, action: "delete" });
      try {
        await axios.delete(`${server}/fee-structure/deleteById`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          params: {
            feeStructureId: id,
          },
        });
        toast.success("Fee structure deleted successfully");
        fetchFeeStructures();
      } catch (error) {
        toast.error("Failed to delete fee structure");
      } finally {
        setActionLoading(null);
      }
    }
  };

  // Helper to resolve courseId from fee object if missing
  const getResolvedCourseId = (fee) => {
    if (fee.courseId) return fee.courseId;
    const course = courses.find(
      (c) => c.courseName === fee.courseName && c.courseCode === fee.courseCode
    );
    return course ? course.id : null;
  };

  const handleDuplicate = async (fee) => {
    const courseId = getResolvedCourseId(fee);

    // If we still can't find a course ID, we might not be able to duplicate correctly
    // But we'll try sending what we have or 0 if the backend accepts it

    setActionLoading({ id: fee.id, action: "duplicate" });

    const payload = {
      courseId: courseId || 0,
      amount: fee.amount,
      description: fee.description,
      installments: fee.installments,
      discountPercentage: fee.discountPercentage,
      lateFee: fee.lateFee,
      isActive: fee.isActive,
    };

    try {
      await axios.post(`${server}/fee-structure/new`, payload, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      toast.success("Fee structure duplicated successfully!");
      fetchFeeStructures();
    } catch (error) {
      toast.error("Failed to duplicate fee structure");
    } finally {
      setActionLoading(null);
    }
  };

  const toggleStatus = async (id) => {
    const feeToUpdate = feeStructures.find((f) => f.id === id);
    if (!feeToUpdate) return;

    setActionLoading({ id, action: "toggle" });

    const courseId = getResolvedCourseId(feeToUpdate);

    const payload = {
      courseId: courseId || 0,
      amount: feeToUpdate.amount,
      description: feeToUpdate.description,
      installments: feeToUpdate.installments,
      discountPercentage: feeToUpdate.discountPercentage,
      lateFee: feeToUpdate.lateFee,
      isActive: !feeToUpdate.isActive,
    };

    try {
      await axios.put(`${server}/fee-structure/updateById`, payload, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        params: {
          feeStructureId: id,
        },
      });
      toast.success("Status updated successfully");
      fetchFeeStructures();
    } catch (error) {
      toast.error("Failed to update status");
    } finally {
      setActionLoading(null);
    }
  };

  const handleEdit = (fee) => {
    const courseId = getResolvedCourseId(fee);

    setEditingFee(fee);
    setFormData({
      courseId: courseId ? courseId.toString() : "",
      amount: fee.amount.toString(),
      description: fee.description || "",
      installments: fee.installments.toString(),
      discountPercentage: (fee.discountPercentage || 0).toString(),
      lateFee: (fee.lateFee || "").toString(),
      isActive: fee.isActive !== false,
    });
    setShowCreateModal(true);
  };

  const filteredFees = feeStructures.filter((fee) => {
    // Use direct properties if available, fallback to lookup
    const courseName = fee.courseName || getCourseName(fee.courseId);
    const courseCode = fee.courseCode || getCourseCode(fee.courseId);
    const matchesSearch = `${courseName} ${courseCode} ${fee.description || ""}`
      .toLowerCase()
      .includes(searchQuery.toLowerCase());
    const matchesFilter =
      filterStatus === "all" ||
      (filterStatus === "active" && fee.isActive) ||
      (filterStatus === "inactive" && !fee.isActive);
    return matchesSearch && matchesFilter;
  });

  // Stats
  const totalStructures = feeStructures.length;
  const activeStructures = feeStructures.filter((f) => f.isActive).length;
  const totalRevenuePotential = feeStructures.reduce(
    (sum, f) => sum + f.amount,
    0
  );
  const avgDiscount =
    feeStructures.reduce((sum, f) => sum + (f.discountPercentage || 0), 0) /
      feeStructures.length || 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 p-6">
      <motion.div
        className="max-w-7xl mx-auto space-y-6"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <div className="p-3 bg-gradient-to-br from-green-600 to-emerald-600 rounded-2xl shadow-lg shadow-green-500/20">
                <DollarSign className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1 className="text-3xl md:text-4xl font-bold bg-gradient-to-br from-green-400 to-emerald-400 bg-clip-text text-transparent">
                  Fee Structure
                </h1>
                <p className="text-gray-400 mt-1">
                  Manage admission fees, installment plans & discounts
                </p>
              </div>
            </div>
          </div>

          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => {
              resetForm();
              setShowCreateModal(true);
            }}
            className="flex items-center gap-2 px-6 py-3 bg-gradient-to-br from-green-600 to-emerald-600 text-white rounded-xl hover:shadow-lg hover:shadow-green-500/25 transition-all font-semibold"
          >
            <Plus className="w-5 h-5" />
            Add New Structure
          </motion.button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <motion.div
            whileHover={{ y: -2 }}
            className="bg-gradient-to-br from-gray-800 to-gray-800/50 rounded-xl p-5 border border-gray-700/50 backdrop-blur-sm"
          >
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-blue-500/20 rounded-lg">
                <Layers className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <p className="text-gray-400 text-sm">Total Structures</p>
                <p className="text-2xl font-bold text-white">
                  {totalStructures}
                </p>
              </div>
            </div>
          </motion.div>

          <motion.div
            whileHover={{ y: -2 }}
            className="bg-gradient-to-br from-gray-800 to-gray-800/50 rounded-xl p-5 border border-gray-700/50 backdrop-blur-sm"
          >
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-green-500/20 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-400" />
              </div>
              <div>
                <p className="text-gray-400 text-sm">Active</p>
                <p className="text-2xl font-bold text-white">
                  {activeStructures}
                </p>
              </div>
            </div>
          </motion.div>

          <motion.div
            whileHover={{ y: -2 }}
            className="bg-gradient-to-br from-gray-800 to-gray-800/50 rounded-xl p-5 border border-gray-700/50 backdrop-blur-sm"
          >
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-emerald-500/20 rounded-lg">
                <IndianRupee className="w-5 h-5 text-emerald-400" />
              </div>
              <div>
                <p className="text-gray-400 text-sm">Total Revenue Potential</p>
                <p className="text-2xl font-bold text-white">
                  ₹{totalRevenuePotential.toLocaleString()}
                </p>
              </div>
            </div>
          </motion.div>

          <motion.div
            whileHover={{ y: -2 }}
            className="bg-gradient-to-br from-gray-800 to-gray-800/50 rounded-xl p-5 border border-gray-700/50 backdrop-blur-sm"
          >
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-purple-500/20 rounded-lg">
                <BadgePercent className="w-5 h-5 text-purple-400" />
              </div>
              <div>
                <p className="text-gray-400 text-sm">Avg Discount</p>
                <p className="text-2xl font-bold text-white">
                  {avgDiscount.toFixed(1)}%
                </p>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Search & Filter */}
        <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-700/50 backdrop-blur-sm">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by course name, code or description..."
                className="w-full pl-10 pr-4 py-3 bg-gray-900/80 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-green-500 focus:ring-1 focus:ring-green-500/50 transition-all"
              />
            </div>
            <div className="relative">
              <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="pl-10 pr-10 py-3 bg-gray-900/80 border border-gray-700 rounded-xl text-white appearance-none cursor-pointer focus:outline-none focus:border-green-500 transition-all min-w-[160px]"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
              <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5 pointer-events-none" />
            </div>
          </div>
        </div>

        {/* Fee Structures Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <RefreshCw className="w-8 h-8 text-green-400 animate-spin" />
          </div>
        ) : filteredFees.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center py-16 bg-gray-800/30 rounded-2xl border border-gray-700/50"
          >
            <DollarSign className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400 text-lg mb-2">
              No fee structures found
            </p>
            <p className="text-gray-500 text-sm mb-4">
              {searchQuery
                ? "Try adjusting your search query"
                : "Create your first fee structure to get started"}
            </p>
            {!searchQuery && (
              <button
                onClick={() => {
                  resetForm();
                  setShowCreateModal(true);
                }}
                className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                Create First Structure
              </button>
            )}
          </motion.div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            <AnimatePresence>
              {filteredFees.map((fee, index) => (
                <FeeCard
                  key={fee.id}
                  fee={fee}
                  index={index}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  onDuplicate={handleDuplicate}
                  onToggleStatus={toggleStatus}
                  onView={setViewingFee}
                  actionLoading={actionLoading}
                />
              ))}
            </AnimatePresence>
          </div>
        )}
      </motion.div>

      {/* Create/Edit Modal */}
      <AnimatePresence>
        {showCreateModal && (
          <FeeModal
            formData={formData}
            setFormData={setFormData}
            onClose={() => {
              setShowCreateModal(false);
              resetForm();
            }}
            onSave={handleSaveFee}
            isEditing={!!editingFee}
            courses={courses}
            loadingCourses={loadingCourses}
            submitting={submitting}
          />
        )}
      </AnimatePresence>

      {/* View Details Modal */}
      <AnimatePresence>
        {viewingFee && (
          <FeeDetailsModal
            fee={viewingFee}
            onClose={() => setViewingFee(null)}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

// Fee Card Component
const FeeCard = ({
  fee,
  index,
  onEdit,
  onDelete,
  onDuplicate,
  onToggleStatus,
  onView,
  actionLoading,
}) => {
  const installmentAmount =
    fee.installmentAmount || Math.round(fee.amount / fee.installments);
  const discountedAmount =
    fee.discountedAmount ||
    fee.amount - (fee.amount * (fee.discountPercentage || 0)) / 100;

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.9 }}
      transition={{ delay: index * 0.05 }}
      whileHover={{ y: -4, transition: { duration: 0.2 } }}
      className={`bg-gradient-to-br from-gray-800 to-gray-800/70 rounded-2xl p-6 border transition-all ${
        fee.isActive
          ? "border-gray-700/50 hover:border-green-500/50 hover:shadow-lg hover:shadow-green-500/10"
          : "border-gray-700/30 opacity-75"
      }`}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1 min-w-0">
          <h3 className="text-lg font-bold text-white truncate mb-1">
            {fee?.courseName}
          </h3>
          {fee?.courseCode && (
            <p className="text-green-400 text-sm font-medium">
              {fee?.courseCode}
            </p>
          )}
        </div>
        <motion.button
          whileTap={{ scale: 0.9 }}
          onClick={() => onToggleStatus(fee.id)}
          disabled={
            actionLoading?.id === fee.id && actionLoading?.action === "toggle"
          }
          className={`px-3 py-1 rounded-full text-xs font-semibold transition-all ${
            fee.isActive
              ? "bg-green-500/20 text-green-400 hover:bg-green-500/30"
              : "bg-gray-600/20 text-gray-400 hover:bg-gray-600/30"
          } ${
            actionLoading?.id === fee.id && actionLoading?.action === "toggle"
              ? "opacity-50 cursor-not-allowed"
              : ""
          }`}
        >
          {actionLoading?.id === fee.id &&
          actionLoading?.action === "toggle" ? (
            <RefreshCw className="w-3 h-3 animate-spin" />
          ) : fee.isActive ? (
            "Active"
          ) : (
            "Inactive"
          )}
        </motion.button>
      </div>

      {/* Description */}
      {fee.description && (
        <p className="text-gray-400 text-sm line-clamp-2 mb-4">
          {fee.description}
        </p>
      )}

      {/* Fee Details */}
      <div className="space-y-3 mb-5">
        <div className="flex items-center justify-between p-3 bg-gray-900/50 rounded-xl">
          <div className="flex items-center gap-2">
            <IndianRupee className="w-4 h-4 text-emerald-400" />
            <span className="text-gray-400 text-sm">Total Amount</span>
          </div>
          <span className="text-white font-bold text-lg">
            ₹{fee?.amount.toLocaleString()}
          </span>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="p-3 bg-gray-900/50 rounded-xl">
            <div className="flex items-center gap-1.5 mb-1">
              <CreditCard className="w-3.5 h-3.5 text-blue-400" />
              <span className="text-gray-500 text-xs">Installments</span>
            </div>
            <p className="text-white font-semibold">{fee.installments}x</p>
            <p className="text-gray-400 text-xs">
              ₹{installmentAmount.toLocaleString()}/each
            </p>
          </div>

          <div className="p-3 bg-gray-900/50 rounded-xl">
            <div className="flex items-center gap-1.5 mb-1">
              <BadgePercent className="w-3.5 h-3.5 text-purple-400" />
              <span className="text-gray-500 text-xs">Discount</span>
            </div>
            <p className="text-white font-semibold">
              {fee.discountPercentage || 0}%
            </p>
            <p className="text-green-400 text-xs">
              ₹{discountedAmount.toLocaleString()}
            </p>
          </div>
        </div>

        {fee.lateFee > 0 && (
          <div className="flex items-center justify-between px-3 py-2 bg-red-500/10 rounded-lg">
            <span className="text-gray-400 text-xs flex items-center gap-1.5">
              <AlertCircle className="w-3.5 h-3.5 text-red-400" />
              Late Payment Fee
            </span>
            <span className="text-red-400 font-medium text-sm">
              ₹{fee.lateFee}
            </span>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex gap-2">
        <motion.button
          whileTap={{ scale: 0.95 }}
          onClick={() => onView(fee)}
          className="p-2.5 text-gray-400 hover:text-white hover:bg-gray-700/50 rounded-lg transition-colors"
          title="View Details"
        >
          <Eye className="w-4 h-4" />
        </motion.button>
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => onDuplicate(fee)}
          disabled={
            actionLoading?.id === fee.id &&
            actionLoading?.action === "duplicate"
          }
          className="p-2 bg-purple-500/10 text-purple-400 hover:bg-purple-500/20 rounded-lg transition-colors"
          title="Duplicate"
        >
          {actionLoading?.id === fee.id &&
          actionLoading?.action === "duplicate" ? (
            <RefreshCw className="w-5 h-5 animate-spin" />
          ) : (
            <Copy className="w-5 h-5" />
          )}
        </motion.button>
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => onEdit(fee)}
          className="p-2 bg-blue-500/10 text-blue-400 hover:bg-blue-500/20 rounded-lg transition-colors"
          title="Edit"
        >
          <Edit className="w-5 h-5" />
        </motion.button>
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => onDelete(fee.id)}
          disabled={
            actionLoading?.id === fee.id && actionLoading?.action === "delete"
          }
          className="p-2 bg-red-500/10 text-red-400 hover:bg-red-500/20 rounded-lg transition-colors"
          title="Delete"
        >
          {actionLoading?.id === fee.id &&
          actionLoading?.action === "delete" ? (
            <RefreshCw className="w-5 h-5 animate-spin" />
          ) : (
            <Trash2 className="w-5 h-5" />
          )}
        </motion.button>
      </div>
    </motion.div>
  );
};

// Create/Edit Modal Component
const FeeModal = ({
  formData,
  setFormData,
  onClose,
  onSave,
  isEditing,
  courses,
  loadingCourses,
  submitting,
}) => {
  const installmentOptions = [1, 2, 3, 4, 6, 12];

  // Find selected course to display code automatically
  const selectedCourse = courses?.find(
    (c) => c.id === Number(formData.courseId)
  );

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.9, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.9, y: 20 }}
        transition={{ type: "spring", damping: 25, stiffness: 300 }}
        className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-gray-700/50 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-gradient-to-br from-green-600 to-emerald-600 rounded-xl">
              <DollarSign className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">
                {isEditing ? "Edit Fee Structure" : "Add New Fee Structure"}
              </h2>
              <p className="text-gray-400 text-sm">
                {isEditing
                  ? "Update the fee details below"
                  : "Fill in the details to create a new structure"}
              </p>
            </div>
          </div>
          <motion.button
            whileHover={{ rotate: 90 }}
            whileTap={{ scale: 0.9 }}
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-white hover:bg-gray-700/50 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </motion.button>
        </div>

        {/* Form */}
        <div className="space-y-5">
          {/* Course Selection */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="flex items-center gap-2 text-sm font-semibold text-gray-300 mb-2">
                <BookOpen className="w-4 h-4 text-green-400" />
                Select Course <span className="text-red-400">*</span>
              </label>
              <div className="relative">
                <select
                  value={formData.courseId}
                  onChange={(e) =>
                    setFormData({ ...formData, courseId: e.target.value })
                  }
                  className="w-full pl-4 pr-10 py-3 bg-gray-900/80 border border-gray-700 rounded-xl text-white appearance-none cursor-pointer focus:outline-none focus:border-green-500 transition-all"
                  disabled={loadingCourses}
                >
                  <option value="">Select a Course</option>
                  {courses?.map((course) => (
                    <option key={course.id} value={course.id}>
                      {course.courseName}
                    </option>
                  ))}
                </select>
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                  {loadingCourses ? (
                    <RefreshCw className="w-5 h-5 text-gray-400 animate-spin" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-400" />
                  )}
                </div>
              </div>
            </div>
            <div>
              <label className="flex items-center gap-2 text-sm font-semibold text-gray-300 mb-2">
                <FileText className="w-4 h-4 text-blue-400" />
                Course Code
              </label>
              <input
                type="text"
                value={selectedCourse?.courseCode || ""}
                readOnly
                className="w-full px-4 py-3 bg-gray-800/50 border border-gray-700 rounded-xl text-gray-400 focus:outline-none cursor-not-allowed"
                placeholder="Auto-populated"
              />
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="flex items-center gap-2 text-sm font-semibold text-gray-300 mb-2">
              <FileText className="w-4 h-4 text-purple-400" />
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
              rows="3"
              className="w-full px-4 py-3 bg-gray-900/80 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-green-500 focus:ring-1 focus:ring-green-500/50 resize-none transition-all"
              placeholder="Brief description of this fee structure..."
            />
          </div>

          {/* Amount & Installments */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="flex items-center gap-2 text-sm font-semibold text-gray-300 mb-2">
                <IndianRupee className="w-4 h-4 text-emerald-400" />
                Total Amount (₹) <span className="text-red-400">*</span>
              </label>
              <input
                type="number"
                value={formData.amount}
                onChange={(e) =>
                  setFormData({ ...formData, amount: e.target.value })
                }
                className="w-full px-4 py-3 bg-gray-900/80 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-green-500 focus:ring-1 focus:ring-green-500/50 transition-all"
                placeholder="50000"
                min="0"
              />
            </div>
            <div>
              <label className="flex items-center gap-2 text-sm font-semibold text-gray-300 mb-2">
                <CreditCard className="w-4 h-4 text-blue-400" />
                Installments
              </label>
              <div className="relative">
                <select
                  value={formData.installments}
                  onChange={(e) =>
                    setFormData({ ...formData, installments: e.target.value })
                  }
                  className="w-full px-4 py-3 bg-gray-900/80 border border-gray-700 rounded-xl text-white appearance-none cursor-pointer focus:outline-none focus:border-green-500 transition-all"
                >
                  {installmentOptions.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt} {opt === 1 ? "Payment" : "Installments"}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5 pointer-events-none" />
              </div>
            </div>
          </div>

          {/* Installment Preview */}
          {formData.amount && formData.installments && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              className="p-4 bg-green-500/10 border border-green-500/20 rounded-xl"
            >
              <div className="flex items-center justify-between">
                <span className="text-green-400 text-sm flex items-center gap-2">
                  <TrendingUp className="w-4 h-4" />
                  Per Installment Amount
                </span>
                <span className="text-white font-bold">
                  ₹
                  {Math.round(
                    Number(formData.amount) / Number(formData.installments)
                  ).toLocaleString()}
                </span>
              </div>
            </motion.div>
          )}

          {/* Discount & Late Fee */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="flex items-center gap-2 text-sm font-semibold text-gray-300 mb-2">
                <BadgePercent className="w-4 h-4 text-purple-400" />
                Discount (%)
              </label>
              <input
                type="number"
                value={formData.discountPercentage}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    discountPercentage: e.target.value,
                  })
                }
                className="w-full px-4 py-3 bg-gray-900/80 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-green-500 focus:ring-1 focus:ring-green-500/50 transition-all"
                placeholder="10"
                min="0"
                max="100"
              />
            </div>
            <div>
              <label className="flex items-center gap-2 text-sm font-semibold text-gray-300 mb-2">
                <AlertCircle className="w-4 h-4 text-red-400" />
                Late Payment Fee (₹)
              </label>
              <input
                type="number"
                value={formData.lateFee}
                onChange={(e) =>
                  setFormData({ ...formData, lateFee: e.target.value })
                }
                className="w-full px-4 py-3 bg-gray-900/80 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-green-500 focus:ring-1 focus:ring-green-500/50 transition-all"
                placeholder="500"
                min="0"
              />
            </div>
          </div>

          {/* Discount Preview */}
          {formData.amount && formData.discountPercentage > 0 && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              className="p-4 bg-purple-500/10 border border-purple-500/20 rounded-xl"
            >
              <div className="flex items-center justify-between">
                <span className="text-purple-400 text-sm flex items-center gap-2">
                  <BadgePercent className="w-4 h-4" />
                  After {formData.discountPercentage}% Discount
                </span>
                <span className="text-white font-bold">
                  ₹
                  {Math.round(
                    Number(formData.amount) *
                      (1 - Number(formData.discountPercentage) / 100)
                  ).toLocaleString()}
                </span>
              </div>
            </motion.div>
          )}

          {/* Status Toggle */}
          <div className="flex items-center justify-between p-4 bg-gray-900/50 rounded-xl">
            <div className="flex items-center gap-3">
              <CheckCircle
                className={`w-5 h-5 ${
                  formData.isActive ? "text-green-400" : "text-gray-500"
                }`}
              />
              <div>
                <p className="text-white font-medium">Active Status</p>
                <p className="text-gray-400 text-sm">
                  Make this fee structure available for use
                </p>
              </div>
            </div>
            <motion.button
              whileTap={{ scale: 0.95 }}
              onClick={() =>
                setFormData({ ...formData, isActive: !formData.isActive })
              }
              className={`relative w-12 h-6 rounded-full transition-colors ${
                formData.isActive ? "bg-green-500" : "bg-gray-600"
              }`}
            >
              <motion.div
                layout
                className="absolute top-1 w-4 h-4 bg-white rounded-full shadow-lg"
                style={{
                  left: formData.isActive ? "calc(100% - 20px)" : "4px",
                }}
              />
            </motion.button>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-4 mt-6 pt-6 border-t border-gray-700/50">
          <motion.button
            whileHover={{ scale: 1.01 }}
            whileTap={{ scale: 0.99 }}
            onClick={onClose}
            className="flex-1 px-6 py-3 bg-gray-700/50 text-white rounded-xl hover:bg-gray-700 transition-colors font-medium"
          >
            Cancel
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={onSave}
            disabled={submitting}
            className={`px-6 py-2 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-xl hover:shadow-lg hover:shadow-green-500/20 transition-all font-semibold ${
              submitting ? "opacity-70 cursor-not-allowed" : ""
            }`}
          >
            {submitting ? (
              <div className="flex items-center gap-2">
                <RefreshCw className="w-4 h-4 animate-spin" />
                {isEditing ? "Updating..." : "Creating..."}
              </div>
            ) : isEditing ? (
              "Update Structure"
            ) : (
              "Create Structure"
            )}
          </motion.button>
        </div>
      </motion.div>
    </motion.div>
  );
};

// View Details Modal Component
const FeeDetailsModal = ({ fee, onClose }) => {
  const installmentAmount =
    fee.installmentAmount || Math.round(fee.amount / fee.installments);
  const discountedAmount =
    fee.discountedAmount ||
    fee.amount - (fee.amount * (fee.discountPercentage || 0)) / 100;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.9, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.9, y: 20 }}
        transition={{ type: "spring", damping: 25, stiffness: 300 }}
        className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl p-6 max-w-lg w-full border border-gray-700/50 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-white mb-1">
              {fee?.courseName}
            </h2>
            {fee.courseCode && (
              <p className="text-green-400 font-medium">{fee?.courseCode}</p>
            )}
          </div>
          <motion.button
            whileHover={{ rotate: 90 }}
            whileTap={{ scale: 0.9 }}
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-white hover:bg-gray-700/50 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </motion.button>
        </div>

        {/* Status */}
        <div className="flex items-center gap-2 mb-4">
          <span
            className={`px-3 py-1 rounded-full text-xs font-semibold ${
              fee.isActive
                ? "bg-green-500/20 text-green-400"
                : "bg-gray-600/20 text-gray-400"
            }`}
          >
            {fee?.isActive ? "Active" : "Inactive"}
          </span>
          {fee.createdAt && (
            <span className="text-gray-500 text-xs flex items-center gap-1">
              <Calendar className="w-3 h-3" />
              Created: {fee.createdAt}
            </span>
          )}
        </div>

        {/* Description */}
        {fee.description && (
          <p className="text-gray-400 text-sm mb-6 p-4 bg-gray-900/50 rounded-xl">
            {fee.description}
          </p>
        )}

        {/* Fee Details Grid */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div className="p-4 bg-gray-900/50 rounded-xl">
            <div className="flex items-center gap-2 mb-2">
              <IndianRupee className="w-4 h-4 text-emerald-400" />
              <span className="text-gray-400 text-sm">Total Amount</span>
            </div>
            <p className="text-white font-bold text-xl">
              ₹{fee.amount.toLocaleString()}
            </p>
          </div>

          <div className="p-4 bg-gray-900/50 rounded-xl">
            <div className="flex items-center gap-2 mb-2">
              <CreditCard className="w-4 h-4 text-blue-400" />
              <span className="text-gray-400 text-sm">Installments</span>
            </div>
            <p className="text-white font-bold text-xl">{fee.installments}x</p>
            <p className="text-gray-400 text-xs">
              ₹{installmentAmount.toLocaleString()} each
            </p>
          </div>

          <div className="p-4 bg-gray-900/50 rounded-xl">
            <div className="flex items-center gap-2 mb-2">
              <BadgePercent className="w-4 h-4 text-purple-400" />
              <span className="text-gray-400 text-sm">Discount</span>
            </div>
            <p className="text-white font-bold text-xl">
              {fee.discountPercentage || 0}%
            </p>
            <p className="text-green-400 text-xs">
              ₹{discountedAmount.toLocaleString()} after
            </p>
          </div>

          <div className="p-4 bg-gray-900/50 rounded-xl">
            <div className="flex items-center gap-2 mb-2">
              <AlertCircle className="w-4 h-4 text-red-400" />
              <span className="text-gray-400 text-sm">Late Fee</span>
            </div>
            <p className="text-white font-bold text-xl">
              {fee.lateFee ? `₹${fee.lateFee}` : "0"}
            </p>
          </div>
        </div>

        {/* Close Button */}
        <motion.button
          whileHover={{ scale: 1.01 }}
          whileTap={{ scale: 0.99 }}
          onClick={onClose}
          className="w-full px-6 py-3 bg-gray-700/50 text-white rounded-xl hover:bg-gray-700 transition-colors font-medium"
        >
          Close
        </motion.button>
      </motion.div>
    </motion.div>
  );
};

export default FeeStructurePage;
